import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeInput, validateEmail, rateLimit } from '@/utils/validation';
import { productionLogger } from '@/utils';
import { useAuth } from '@/components/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';

interface NewsletterSubscribeProps {
  className?: string;
  placeholder?: string;
  variant?: 'inline' | 'stacked';
}

interface SubscriptionResponse {
  message?: string;
}

export const NewsletterSubscribe = ({
  className = '',
  placeholder = 'Enter your email',
  variant = 'inline'
}: NewsletterSubscribeProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(user?.email ?? '');
  const [isLoading, setIsLoading] = useState(false);

  // Sign-in prompt for unauthenticated users
  if (!user) {
    return (
      <div className={`flex flex-col sm:flex-row gap-2 ${className}`}>
        <Button
          type="button"
          onClick={() => navigate('/auth')}
          className="w-full sm:w-auto sm:mx-auto"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Sign in to subscribe
        </Button>
      </div>
    );
  }

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      toast.error('Invalid email', {
        description: 'Please enter a valid email address'
      });
      return;
    }

    if (!rateLimit(`newsletter-${trimmedEmail}`, 5, 3600000)) {
      toast.error('Too many attempts', {
        description: 'Please wait before subscribing again'
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('newsletter-subscribe', {
        body: {
          email: trimmedEmail.toLowerCase(),
          source: 'website',
          user_id: user.id,
        }
      });

      if (error) {
        productionLogger.error('Subscription error:', error);
        throw error;
      }

      const responseData = data as SubscriptionResponse;
      toast.success('Success!', {
        description: responseData?.message || 'Thank you for subscribing to our newsletter!'
      });
    } catch (error) {
      productionLogger.error('Error subscribing to newsletter:', error);
      toast.error('Subscription Failed', {
        description: 'There was an error subscribing to our newsletter. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'stacked') {
    return (
      <form onSubmit={handleSubscribe} className={`space-y-3 ${className}`}>
        <Input
          id="newsletter-subscribe-stacked"
          name="newsletter-email"
          type="email"
          placeholder={placeholder}
          value={email}
          onChange={(e) => setEmail(sanitizeInput(e.target.value, 254))}
          disabled={isLoading}
          maxLength={254}
          className="w-full"
        />
        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubscribe} className={`flex gap-2 ${className}`}>
      <Input
        id="newsletter-subscribe-inline"
        name="newsletter-email"
        type="email"
        placeholder={placeholder}
        value={email}
        onChange={(e) => setEmail(sanitizeInput(e.target.value, 254))}
        disabled={isLoading}
        maxLength={254}
        className="flex-1"
      />
      <Button type="submit" disabled={isLoading}>
        {isLoading ? 'Subscribing...' : 'Subscribe'}
      </Button>
    </form>
  );
};
